import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions } from '@react-navigation/native';
import { Stack, useLocalSearchParams, useNavigation, usePathname, useRouter } from 'expo-router';
import React, { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundLeafletingGeometry, useBackgroundLeafletingRoutes, useBackgroundMyActiveLeafletingRoutes } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useLeafletingStore } from '@/stores/leafletingStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { LeafletingRouteAssignment } from '@/types/leafleting';
import { calculateMapRegionFromFeatureCollection, extractPolygonsFromFeatureCollection } from '@/utils/geojson';
import { calculatePolygonCenter } from '@/utils/geometry';

// Lazy load the map component for better performance
const CrossPlatformMapView = lazy(() => import('@/components/MapView'));


export default function LeafletingRoundDetailsScreen() {
  const { roundId } = useLocalSearchParams<{ roundId: string }>();
  const [tab, setTab] = useState<'list' | 'map'>('list');
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const pathname = usePathname();
  
  // Theme-aware colors for tabs
  const backgroundColor = useThemeColor({}, 'background');
  const borderColor = useThemeColor({}, 'border' as any);
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const iconColor = useThemeColor({}, 'icon');
  const secondaryTextColor = useThemeColor({}, 'secondaryText');
  const tintColor = useThemeColor({}, 'tint');
  const progressHigh = useThemeColor({}, 'progressHigh');
  const progressMedium = useThemeColor({}, 'progressMedium');
  const progressLow = useThemeColor({}, 'progressLow');
  const progressAssigned = useThemeColor({}, 'progressAssigned');

  // Platform-aware color utility
  const getPlatformColor = (color: string, opacity: number = 0.3) => {
    // Convert hex color to rgba for both web and native
    // React Native maps now support rgba colors directly
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // If already rgba or rgb, handle it
    if (color.startsWith('rgb')) {
      return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }
    return color;
  };

  // Utility function to get progress-based colors for leafleting assignments
  const getAssignmentProgressColors = (assignment: LeafletingRouteAssignment) => {
    // Use the same color scheme as the higher-level leafleting grouping
    switch (assignment.status) {
      case 'completed':
        return {
          fillColor: getPlatformColor(progressHigh, 0.3), // Green
          strokeColor: progressHigh // Green border
        };
      case 'in_progress':
        return {
          fillColor: getPlatformColor(progressMedium, 0.3), // Orange
          strokeColor: progressMedium // Orange border
        };
      case 'assigned':
        return {
          fillColor: getPlatformColor(progressAssigned, 0.3), // Red
          strokeColor: progressAssigned // Red border
        };
      case 'unassigned':
      case 'not_doing':
      default:
        return {
          fillColor: getPlatformColor(progressLow, 0.3), // Gray
          strokeColor: progressLow // Gray border
        };
    }
  };
  
  // Use background fetch hooks for route assignments
  const { data: allRoutes, loading: allRoutesLoading, error: allRoutesError } = useBackgroundLeafletingRoutes(roundId);
  const { data: myRoutes, loading: myRoutesLoading, error: myRoutesError } = useBackgroundMyActiveLeafletingRoutes();
  
  // Sort routes by route number for consistent display order
  const sortedRoutes = useMemo(() => {
    if (!allRoutes) return [];
    return [...allRoutes].sort((a, b) => (a.number_within_branch || 0) - (b.number_within_branch || 0));
  }, [allRoutes]);
  
  // Get assignment IDs for geometry fetching - only when on map tab and routes are available
  const assignmentIds = useMemo(() => {
    // Only generate assignment IDs when user is on map tab to trigger geometry fetch
    if (tab !== 'map' || !allRoutes) {
      return [];
    }
    
    const ids = allRoutes.map((route: LeafletingRouteAssignment) => route.assignment_id);
    return ids;
  }, [tab, allRoutes]);
  
  // Fetch geometry only when on map tab and we have assignment IDs
  const { data: allAssignmentMapDetails, loading: geometryLoading, error: geometryError } = useBackgroundLeafletingGeometry(
    roundId!, 
    assignmentIds
  );
  
  
  // Filter assignment map details to only include the requested assignment IDs
  const assignmentMapDetails = useMemo(() => {
    if (!allAssignmentMapDetails || !assignmentIds.length) {
      return null;
    }
    
    const filteredDetails: { [assignmentId: string]: any } = {};
    assignmentIds.forEach((assignmentId: string) => {
      if (allAssignmentMapDetails[assignmentId]) {
        filteredDetails[assignmentId] = allAssignmentMapDetails[assignmentId];
      }
    });
    
    return Object.keys(filteredDetails).length > 0 ? filteredDetails : null;
  }, [allAssignmentMapDetails, assignmentIds]);

  // Convert assignment map details to GeoJSON FeatureCollection for map region calculation
  const geometryFeatureCollection = useMemo(() => {
    if (!assignmentMapDetails) {
      return null;
    }
    
    const features: any[] = [];
    Object.entries(assignmentMapDetails).forEach(([assignmentId, details]) => {
      if (details.geometry) {
        // The leafleting API returns geometry as a FeatureCollection, so we need to extract the actual features
        if (details.geometry.type === 'FeatureCollection' && details.geometry.features) {
          // Add each feature from the FeatureCollection, but update the properties to include assignment_id
          details.geometry.features.forEach((feature: any) => {
            const updatedFeature = {
              ...feature,
              properties: {
                ...feature.properties,
                assignment_id: assignmentId
              }
            };
            features.push(updatedFeature);
          });
        } else {
          // Fallback for direct geometry (shouldn't happen with current API)
          const feature = {
            type: 'Feature',
            id: assignmentId,
            geometry: details.geometry,
            properties: {
              assignment_id: assignmentId
            }
          };
          features.push(feature);
        }
      }
    });
    
    const featureCollection = features.length > 0 ? {
      type: 'FeatureCollection',
      features
    } : null;
    
    return featureCollection;
  }, [assignmentMapDetails]);

  // Calculate initial map region from geometry - only when geometry is loaded
  const initialRegion = useMemo(() => {
    
    // Only calculate region when we have geometry data
    if (!geometryFeatureCollection) {
      // Return a default region (London)
      return {
        latitude: 51.505,
        longitude: -0.09,
        latitudeDelta: 0.05,
        longitudeDelta: 0.05,
      };
    }
    
    const region = calculateMapRegionFromFeatureCollection(geometryFeatureCollection, 0.1);
    return region;
  }, [geometryFeatureCollection]);

  // Create polygons for route assignments
  // Only compute when geometry is loaded and we're on map tab
  const polygons: any[] = useMemo(() => {
    // Early return if not on map tab or no geometry loaded - avoid expensive computation
    if (tab !== 'map' || !geometryFeatureCollection || !sortedRoutes) {
      return [];
    }

    const extractedPolygons = extractPolygonsFromFeatureCollection(
      geometryFeatureCollection,
      {
        idPrefix: 'leafleting-polygon',
        strokeColor: '#007AFF',
        fillColor: getPlatformColor('#007AFF', 0.2),
        strokeWidth: 2,
        propertyExtractor: (feature, index) => {
          const assignment = sortedRoutes.find((route: LeafletingRouteAssignment) => 
            route.assignment_id === feature.properties?.assignment_id
          );
          return feature.id || `assignment-${assignment?.assignment_id || 'unknown'}-${index}`;
        },
        styleExtractor: (feature) => {
          const assignment = sortedRoutes.find((route: LeafletingRouteAssignment) => 
            route.assignment_id === feature.properties?.assignment_id
          );
          return assignment ? getAssignmentProgressColors(assignment) : {
            fillColor: getPlatformColor('#007AFF', 0.2),
            strokeColor: '#007AFF'
          };
        },
        onPress: (feature) => {
          const assignment = sortedRoutes.find((route: LeafletingRouteAssignment) => 
            route.assignment_id === feature.properties?.assignment_id
          );
          if (assignment) {
            handleRouteAssignmentPress(assignment);
          }
        },
      }
    );

    return extractedPolygons;
  }, [tab, geometryFeatureCollection, sortedRoutes]);

  // Create markers for route numbers
  // Only compute when geometry is loaded and we're on map tab
  const markers: any[] = useMemo(() => {
    // Early return if not on map tab or no geometry loaded - avoid expensive computation
    if (tab !== 'map' || !geometryFeatureCollection || !geometryFeatureCollection.features || !sortedRoutes) {
      return [];
    }
    
    const polygonFeatures = geometryFeatureCollection.features.filter((feature: any) => feature.geometry && feature.geometry.type === 'Polygon');
    
    const markers = polygonFeatures.map((feature: any, index: number) => {
      const assignment = sortedRoutes.find((route: LeafletingRouteAssignment) => 
        route.assignment_id === feature.properties?.assignment_id
      );
      
      if (!assignment) {
        return null;
      }
      
      // Calculate proper center of polygon using improved geometry utility
      const coordinates = feature.geometry.coordinates[0];
      const center = calculatePolygonCenter(coordinates);
      
      // Use feature.id if available, otherwise use assignment_id and index for uniqueness
      const markerId = feature.id || `marker-assignment-${assignment.assignment_id}-${index}`;
      
      // Determine marker type and color based on active campaigners
      const hasActiveCampaigners = assignment.campaigner_names && assignment.campaigner_names.some((name: string | null) => name !== null && name !== undefined);
      const markerType = hasActiveCampaigners ? 'route-number-active' : 'route-number';
      
      const marker = {
        id: `route-number-${markerId}`,
        coordinate: center,
        title: String(assignment.number_within_branch),
        description: `${assignment.route_name || `Route ${assignment.number_within_branch}`}`,
        engagementId: assignment.assignment_id, // Use engagementId property for compatibility
        routeName: assignment.route_name || `Route ${assignment.number_within_branch}`,
        routeNumber: assignment.number_within_branch,
        type: markerType,
        campaignerNames: assignment.campaigner_names?.filter((name: any): name is string => name !== null && name !== undefined) || [],
      };
      
      return marker;
    }).filter(Boolean);
    
    return markers;
  }, [tab, geometryFeatureCollection, sortedRoutes]);
  
  // Determine overall loading and error states
  const loading = allRoutesLoading || myRoutesLoading || (tab === 'map' && geometryLoading);
  const error = allRoutesError || myRoutesError || (tab === 'map' && geometryError);

  const navigation = useNavigation();

  // Get round information for display
  const rounds = useLeafletingStore(state => state.getRounds());
  const currentRound = rounds?.find(round => round.round_global_id === roundId);

  // Filter my routes to only those in this round
  const myRoutesInRound = useMemo(() => {
    if (!myRoutes || !roundId) return [];
    return myRoutes.filter((route: LeafletingRouteAssignment) => route.round_name === currentRound?.round_name);
  }, [myRoutes, roundId, currentRound]);

  // Filter other routes (not mine)
  const otherRoutes = useMemo(() => {
    if (!sortedRoutes || !myRoutes) return [];
    
    const myRouteIds = new Set(myRoutesInRound.map((route: LeafletingRouteAssignment) => route.assignment_id));
    return sortedRoutes.filter((route: LeafletingRouteAssignment) => 
      !myRouteIds.has(route.assignment_id)
    );
  }, [sortedRoutes, myRoutes, myRoutesInRound]);

  // Create combined data for FlatList with section headers
  const listData = useMemo(() => {
    const data: { type: 'header' | 'route', title?: string, route?: LeafletingRouteAssignment }[] = [];
    
    // Add my routes section
    if (myRoutesInRound.length > 0) {
      data.push({ type: 'header', title: 'My Routes' });
      myRoutesInRound.forEach((route: LeafletingRouteAssignment) => {
        data.push({ type: 'route', route });
      });
    }
    
    // Add other routes section
    if (otherRoutes.length > 0) {
      data.push({ type: 'header', title: 'Other Routes' });
      otherRoutes.forEach((route: LeafletingRouteAssignment) => {
        data.push({ type: 'route', route });
      });
    }
    
    return data;
  }, [myRoutesInRound, otherRoutes]);

  // Handle route assignment selection
  const handleRouteAssignmentPress = useCallback((route: LeafletingRouteAssignment) => {
    // Navigate to assignment details screen
    const { getSelectedBranch, getSelectedCampaign } = useNavigationStore.getState();
    const selectedBranch = getSelectedBranch();
    const selectedCampaign = getSelectedCampaign();
    
    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
      router.push({
        pathname: '/(main)/[branch]/campaign/[campaignId]/leafleting/[roundId]/[assignmentId]',
        params: { 
          branch: selectedBranch.branch_short_name,
          campaignId: selectedCampaign.boundary_id,
          roundId: roundId,
          assignmentId: route.assignment_id
        }
      });
    }
  }, [roundId]);

  // Render route assignment item
  const renderRouteAssignment = useCallback(({ item }: { item: LeafletingRouteAssignment }) => {
    // Get status-based color for progress badge using theme colors
    const getProgressColor = (status: string) => {
      switch (status) {
        case 'completed':
          return progressHigh; // Green
        case 'in_progress':
          return progressMedium; // Orange
        case 'assigned':
          return progressAssigned; // Red
        case 'unassigned':
        case 'not_doing':
        default:
          return progressLow; // Dark grey
      }
    };

    // Calculate progress: 100% if completed, 0% otherwise
    const isCompleted = item.status === 'completed';
    const progressPercentage = isCompleted ? 100 : 0;
    const progressColor = getProgressColor(item.status);
    
    return (
      <TouchableOpacity
        style={[
          AppStyles.listItemLeftAligned,
          { backgroundColor: cardBackground, borderBottomColor: borderColor }
        ]}
        onPress={() => handleRouteAssignmentPress(item)}
      >
        <View style={{ width: '100%' }}>
          {/* Header Row: Route name on left, progress box on right */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <ThemedText style={[AppStyles.headerText, { flex: 1, marginRight: 8 }]}>
              {item.number_within_branch}: {item.route_name || `Route Assignment ${item.assignment_id.slice(-8)}`}
            </ThemedText>
            <View style={{ 
              backgroundColor: progressColor,
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 16,
              minWidth: 60,
              alignItems: 'center'
            }}>
              <ThemedText style={{ color: '#ffffff', fontSize: 14, fontWeight: 'bold' }}>
                {progressPercentage}%
              </ThemedText>
            </View>
          </View>
          
          {/* Address count - left aligned */}
          <ThemedText style={{ fontSize: 12, color: secondaryTextColor}}>
            {item.address_count} addresses
          </ThemedText>
          
          {item.date_started && (
            <ThemedText style={{ color: secondaryTextColor, fontSize: 12, marginTop: 2 }}>
              Started: {new Date(item.date_started).toLocaleDateString()}
            </ThemedText>
          )}
        </View>
      </TouchableOpacity>
    );
  }, [cardBackground, borderColor, secondaryTextColor, progressHigh, progressMedium, progressLow, progressAssigned, handleRouteAssignmentPress]);

  // Render list item with section headers
  const renderListItem = useCallback(({ item }: { item: { type: 'header' | 'route', title?: string, route?: LeafletingRouteAssignment } }) => {
    if (item.type === 'header') {
      return (
        <View style={{ paddingHorizontal: 16, paddingVertical: 12, backgroundColor: cardBackground }}>
          <ThemedText style={AppStyles.headerText}>{item.title}</ThemedText>
        </View>
      );
    } else if (item.type === 'route' && item.route) {
      return renderRouteAssignment({ item: item.route });
    }
    return null;
  }, [renderRouteAssignment, cardBackground]);

  // Key extractor for FlatList
  const keyExtractor = useCallback((item: { type: 'header' | 'route', title?: string, route?: LeafletingRouteAssignment }) => {
    return item.type === 'header' ? `header-${item.title}` : `route-${item.route?.assignment_id}`;
  }, []);

  // Render loading state
  if (loading) {
    return (
      <ThemedView style={[AppStyles.container, { backgroundColor }]}>
        <Stack.Screen
          options={{
            headerTitle: () => (
              <View style={AppStyles.headerContainer}>
                <ThemedText style={AppStyles.headerText}>
                  {currentRound?.round_name || 'Loading...'}
                </ThemedText>
              </View>
            ),
            headerLeft: () => (
              <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
                <IconSymbol name="chevron.right" size={28} color="#007AFF" />
              </TouchableOpacity>
            ),
            headerBackVisible: false,
          }}
        />
        <View style={AppStyles.centeredContainer}>
          <ActivityIndicator size="large" color={iconColor} />
          <ThemedText style={{ marginTop: 16 }}>Loading route assignments...</ThemedText>
        </View>
      </ThemedView>
    );
  }

  // Render error state
  if (error) {
    return (
      <ThemedView style={[AppStyles.container, { backgroundColor }]}>
        <Stack.Screen
          options={{
            headerTitle: () => (
              <View style={AppStyles.headerContainer}>
                <ThemedText style={AppStyles.headerText}>
                  {currentRound?.round_name || 'Error'}
                </ThemedText>
              </View>
            ),
            headerLeft: () => (
              <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
                <IconSymbol name="chevron.right" size={28} color="#007AFF" />
              </TouchableOpacity>
            ),
            headerBackVisible: false,
          }}
        />
        <View style={AppStyles.centeredContainer}>
          <ThemedText style={AppStyles.errorText}>Error loading route assignments: {String(error)}</ThemedText>
        </View>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={[AppStyles.container, { backgroundColor }]}>
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>
                {currentRound?.round_name || 'Route Assignments'}
              </ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color="#007AFF" />
            </TouchableOpacity>
          ),
          headerRight: () => {
            const { getSelectedBranch } = useNavigationStore.getState();
            const selectedBranch = getSelectedBranch();
            const hasGrantPermission = ['commander', 'lieutenant', 'sgt major'].includes((selectedBranch?.branch_role || '').toLowerCase());
            
            if (!hasGrantPermission) return null;
            
            return (
              <TouchableOpacity 
                onPress={() => router.push({ 
                  pathname: '/(main)/[branch]/share', 
                  params: { 
                    branch: selectedBranch?.branch_short_name || "", 
                    ref: pathname,
                    roundId: roundId
                  } 
                })}
                style={{ 
                  padding: 8,
                  marginRight: 12,
                }}
              >
                <IconSymbol name="square.and.arrow.up" size={24} color="#007AFF" />
              </TouchableOpacity>
            );
          },
          headerBackVisible: false,
        }}
      />
      

      {/* Content Area */}
      <View style={{ flex: 1 }}>
        {loading && <ActivityIndicator size="large" color={iconColor} />}
        {error && <ThemedText style={AppStyles.errorText}>{error}</ThemedText>}
        
        {!loading && !error && tab === 'list' && (
          <>
            {!allRoutes || allRoutes.length === 0 ? (
              <View style={AppStyles.emptyContainer}>
                <ThemedText style={AppStyles.noDataText}>No route assignments found</ThemedText>
              </View>
            ) : (
              <FlatList
                data={listData}
                keyExtractor={keyExtractor}
                renderItem={renderListItem}
                contentContainerStyle={{ paddingBottom: 20 }}
              />
            )}
          </>
        )}

        {!loading && !error && tab === 'map' && (
          <View style={{ flex: 1 }}>
            
            {/* Show loading state while geometry is being fetched */}
            {geometryLoading ? (
              <View style={AppStyles.centeredContainer}>
                <ActivityIndicator size="large" color={iconColor} />
                <ThemedText style={{ marginTop: 16 }}>Loading map geometry...</ThemedText>
              </View>
            ) : geometryError ? (
              <View style={AppStyles.centeredContainer}>
                <ThemedText style={AppStyles.errorText}>
                  Error loading map geometry: {geometryError.message}
                </ThemedText>
              </View>
            ) : assignmentMapDetails ? (
              <Suspense fallback={
                <View style={AppStyles.centeredContainer}>
                  <ActivityIndicator size="large" color={iconColor} />
                  <ThemedText style={{ marginTop: 16 }}>Loading map...</ThemedText>
                </View>
              }>
                <CrossPlatformMapView
                  style={{ flex: 1 }}
                  initialRegion={initialRegion}
                  markers={markers}
                  polygons={polygons}
                  showUserLocation={true}
                  showProgressLegend={true}
                  legendItems={[
                    { color: progressHigh, label: 'Completed' },
                    { color: progressMedium, label: 'In Progress' },
                    { color: progressAssigned, label: 'Assigned' },
                    { color: progressLow, label: 'Unassigned' },
                  ]}
                  onMarkerPress={(marker) => {
                    if (marker.engagementId) {
                      const assignment = sortedRoutes?.find((route: LeafletingRouteAssignment) => 
                        route.assignment_id === marker.engagementId
                      );
                      if (assignment) {
                        // For assigned routes, the MapView will show the campaigner popup automatically
                        // For unassigned routes, navigate directly to assignment details
                        if (marker.type === 'route-number') {
                          handleRouteAssignmentPress(assignment);
                        }
                        // For 'route-number-active' markers, the MapView handles the popup
                      }
                    }
                  }}
                />
              </Suspense>
            ) : (
              <View style={AppStyles.centeredContainer}>
                <ActivityIndicator size="large" color={iconColor} />
                <ThemedText style={{ marginTop: 16 }}>Preparing map...</ThemedText>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Bottom Tab Navigation */}
      <View style={{
        flexDirection: 'row',
        backgroundColor: backgroundColor,
        borderTopWidth: 1,
        borderTopColor: borderColor,
        paddingBottom: insets.bottom,
      }}>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            tab === 'list' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setTab('list')}
        >
          <MaterialIcons 
            name="list" 
            size={24} 
            color={tab === 'list' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            tab === 'list' ? { color: tintColor } : { color: iconColor }
          ]}>List</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            tab === 'map' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setTab('map')}
        >
          <MaterialIcons 
            name="map" 
            size={24} 
            color={tab === 'map' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            tab === 'map' ? { color: tintColor } : { color: iconColor }
          ]}>Map</ThemedText>
        </TouchableOpacity>
      </View>
    </ThemedView>
  );
}
